import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageContext";

const IMGS = [
  "https://static.wixstatic.com/media/11062b_78f78a3902974005b16a24114ad52453~mv2.jpg/v1/fill/w_1200,h_900,al_c,q_85,usm_0.66_1.00_0.01/11062b_78f78a3902974005b16a24114ad52453~mv2.jpg",
  "https://static.wixstatic.com/media/11062b_3cc314074dec4cd5891b7bbb0c0b6f12~mv2.jpg/v1/fill/w_1200,h_900,al_c,q_85,usm_0.66_1.00_0.01/11062b_3cc314074dec4cd5891b7bbb0c0b6f12~mv2.jpg",
  "https://static.wixstatic.com/media/6765ec_db5106dd5e264fd1bc5dabac07f98d1b~mv2.png/v1/fill/w_1200,h_900,al_c,q_85,usm_0.66_1.00_0.01/6765ec_db5106dd5e264fd1bc5dabac07f98d1b~mv2.png",
];

export default function Services() {
  const { t, images } = useLanguage();
  return (
    <main className="pt-[72px]">
      <Seo page="services" path="/services" />

      <section className="py-20 sm:py-28 border-b border-[#E3DED4]" data-testid="services-hero-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C6B4F] font-semibold mb-6">{t.services.pageEyebrow}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#142D44] leading-[1.08] max-w-3xl">
            <MaskedLines lines={[t.services.pageTitle]} />
          </h1>
          <p className="mt-7 text-[#4A5568] leading-relaxed max-w-2xl text-lg">{t.services.pageIntro}</p>
        </div>
      </section>

      {t.services.list.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className={`py-24 sm:py-28 ${i % 2 === 1 ? "bg-[#F4F1EA] border-y border-[#E3DED4]" : ""}`}
          data-testid={`service-detail-${s.id}`}
        >
          <div className={`max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center`}>
            <Reveal className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2 lg:col-start-8" : ""}`}>
              <div className="relative">
                <div className="absolute -top-5 -left-5 w-full h-full border border-[#B85D2A]/35 rounded-2xl" />
                <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
                  <img src={images[`service_0${i + 1}`] || IMGS[i]} alt={s.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </div>
            </Reveal>
            <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1 lg:col-start-1" : "lg:col-start-7"}`}>
              <Reveal>
                <span className="font-serif text-6xl text-[#EBE7DF]">{s.number}</span>
                <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight text-[#142D44] leading-tight">{s.title}</h2>
                <p className="mt-3 text-xs font-mono uppercase tracking-[0.16em] text-[#8C6B4F]">{s.tagline}</p>
                <p className="mt-5 text-[#4A5568] leading-relaxed">{s.desc}</p>
                <p className="mt-8 font-mono text-xs uppercase tracking-[0.22em] text-[#8C6B4F] font-semibold">{t.services.deliverablesLabel}</p>
                <ul className="mt-4 space-y-3">
                  {s.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-3 text-sm text-[#4A5568]">
                      <Check size={16} className="text-[#B85D2A] mt-0.5 shrink-0" strokeWidth={2} />
                      {d}
                    </li>
                  ))}
                </ul>
                <div className="mt-9 flex flex-wrap items-center gap-6">
                  <Link
                    to={`/services/${s.id}`}
                    data-testid={`service-fullpage-${s.id}`}
                    className="group inline-flex items-center gap-2 px-6 py-3 bg-[#1E3A5F] text-[#FBF9F5] text-sm font-medium hover:bg-[#B85D2A] transition-colors duration-300"
                  >
                    {t.services.fullPage}
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <Link
                    to="/contact"
                    data-testid={`service-enquire-${s.id}`}
                    className="group inline-flex items-center gap-2 text-sm font-medium text-[#B85D2A]"
                  >
                    {t.services.enquire}
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      <section className="py-24 bg-[#142D44]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#FBF9F5]">{t.cta.title}</h2>
            <p className="mt-5 text-[#9AA6B5] max-w-xl mx-auto">{t.cta.text}</p>
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="group mt-9 inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-[#B85D2A] text-[#FBF9F5] text-sm font-medium hover:bg-[#FBF9F5] hover:text-[#142D44] transition-colors duration-300"
            >
              {t.cta.btn}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
